package com.wintermar.inspection.feature.presentation.inspection.answer.search

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.wintermar.inspection.feature.domain.model.answer.AnswerModel
import com.wintermar.inspection.feature.domain.model.answer.SummaryAnswerModel
import com.wintermar.inspection.feature.domain.model.template.InspectionTemplateModel
import com.wintermar.inspection.feature.presentation.inspection.answer.AnswerState
import com.wintermar.inspection.feature.presentation.inspection.answer.search.util.InspectionSearchQuestionType
import com.wintermar.inspection.graphql.type.InspectionAnswerType
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch

class InspectionSearchQuestionState(
    private val scope: CoroutineScope,
    val inspectionId: String,
    val onSaveAnswer: (
        answer: AnswerModel,
        answerType: InspectionAnswerType,
        includeUploadFile: Boolean
    ) -> Unit,
    val onReattachUploadCallback: () -> Unit
) : AnswerState(
    inspectionId,
    onSaveAnswer,
    onReattachUploadCallback
) {
    var displayData: List<SummaryAnswerModel>? by mutableStateOf(null)
    var displayPageData: List<Pair<String, String>>? by mutableStateOf(null) // id, title
    var displaySectionData: List<Pair<String, String>>? by mutableStateOf(null) // id, title
    var keyword: String by mutableStateOf("")
    var isFiltering by mutableStateOf(false)
    var activeTab by mutableStateOf(InspectionSearchQuestionType.QUESTION)

    val totalResult: Int
        get() = when (activeTab) {
            InspectionSearchQuestionType.QUESTION -> displayData?.size ?: 0
            InspectionSearchQuestionType.PAGE -> displayPageData?.size ?: 0
            InspectionSearchQuestionType.SECTION -> displaySectionData?.size ?: 0
        }

    private var filterJob: Job? = null
    private var templateUpdateJob: Job? = null

    override var template: InspectionTemplateModel
        get() = super.template
        set(value) {
            super.template = value
            // Debounce template updates to avoid excessive filtering
            templateUpdateJob?.cancel()
            templateUpdateJob = scope.launch {
                updateDisplayData()
            }
        }

    private fun InspectionTemplateModel.filterByQuestion(keyword: String): InspectionTemplateModel {
        val allQuestions = this.pages.flatMap { page ->
            page.sections.flatMap { section ->
                section.questions.filter { question ->
                    question.questionText.contains(keyword, ignoreCase = true)
                }.map { question ->
                    Triple(page, section, question)
                }
            }
        }

        if (allQuestions.isEmpty()) return this.copy(pages = emptyList())

        val groupedPages = allQuestions.groupBy { it.first.id }.mapNotNull { (_, items) ->
            val page = items[0].first
            val groupedSections = items.groupBy { it.second.id }.mapNotNull { (_, sectionItems) ->
                val section = sectionItems[0].second
                val questions = sectionItems.map { it.third }
                section.copy(questions = questions)
            }
            if (groupedSections.isNotEmpty()) {
                page.copy(sections = groupedSections)
            } else null
        }

        return this.copy(pages = groupedPages)
    }

    private fun InspectionTemplateModel.filterByPageTitle(keyword: String): InspectionTemplateModel {
        val filteredPages = this.pages.filter { page ->
            page.title.contains(keyword, ignoreCase = true)
        }
        return this.copy(pages = filteredPages)
    }

    private fun InspectionTemplateModel.filterBySection(keyword: String): InspectionTemplateModel {
        val filteredPages = this.pages.mapNotNull { page ->
            val filteredSections = page.sections.filter { section ->
                section.title.contains(keyword, ignoreCase = true)
            }
            if (filteredSections.isNotEmpty()) {
                page.copy(sections = filteredSections)
            } else null
        }
        return this.copy(pages = filteredPages)
    }


    private fun updateDisplayData() {
        if (keyword.isBlank()) {
            displayData = null
            displayPageData = null
            displaySectionData = null
            isFiltering = false
            filterJob?.cancel()
            return
        }

        filterJob?.cancel()
        isFiltering = true

        filterJob = scope.launch(Dispatchers.Default) {
            val filteredQuestions = template.filterByQuestion(keyword).toSummaryAnswerList()
            val filteredPages = template.filterByPageTitle(keyword).pages.map { it.id to it.title }
            val filteredSections =
                template.filterBySection(keyword).pages.flatMap { page -> page.sections }
                    .map { it.id to it.title }

            scope.launch(Dispatchers.Main) {
                displayData = filteredQuestions
                displayPageData = filteredPages
                displaySectionData = filteredSections
                isFiltering = false
            }
        }
    }

    fun setOnKeywordValueChanged(value: CharSequence?) {
        keyword = value.toString()
        updateDisplayData()
    }

    fun onChangeTab(inspectionSearchQuestionType: InspectionSearchQuestionType) {
        activeTab = inspectionSearchQuestionType
    }
}
